import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/data/status-badge";
import { cn } from "@/lib/utils";
import type { MnjOperacaoWithItens, AtividadeCategoria } from "@shared/schema";
import type { GrupoHoje } from "@shared/manejo-hoje";
import { areaAplicadaHa } from "@shared/manejo-calc";
import { tituloExibicaoOperacao } from "@shared/lista-pendencias";
import {
  Droplets, SprayCan, Leaf, Scissors, Wrench, CircleDot,
  Play, Check, AlertTriangle, type LucideIcon,
} from "lucide-react";

// Card da tela Hoje — a unidade de trabalho do operador (spec §6.1).
//
// Desenhado para UM POLEGAR: o corpo inteiro abre o detalhe, e o botão de ação
// ocupa a largura toda, embaixo, longe do que se toca por engano. É o oposto do
// card do kanban, que era feito para o mouse do Hugo conferir a semana.

/**
 * Ícone por categoria da atividade. Só ÍCONE, sem cor: a guarda de CI da Onda 3
 * (Rizoma_UI_Spec.md) proíbe cor fixa fora dos tokens, e categoria não é estado —
 * pintar cada uma de um tom competiria com o vermelho de "atrasada", que é a
 * única cor que precisa gritar nesta tela.
 */
const ICONE_CATEGORIA: Record<AtividadeCategoria, LucideIcon> = {
  fertirrigacao: Droplets,
  pulverizacao: SprayCan,
  foliar: Leaf,
  tratos_culturais: Scissors,
  manutencao: Wrench,
  outro: CircleDot,
};

export function iconeDaCategoria(categoria: string | null | undefined): LucideIcon {
  if (!categoria) return CircleDot;
  return ICONE_CATEGORIA[categoria as AtividadeCategoria] ?? CircleDot;
}

/**
 * O título do card — a MESMA função da Lista, de Minhas e da mensagem do WhatsApp
 * (`tituloExibicaoOperacao`): título próprio ou atividade, e os setores depois do
 * "·". Antes os setores saíam daqui (os chips já os mostravam); o Hugo pediu o
 * contrário, e o texto das telas e o do WhatsApp têm que dizer a mesma coisa.
 */
export function tituloDaOperacao(op: MnjOperacaoWithItens): string {
  return tituloExibicaoOperacao(op);
}

export function areaTotalAplicada(op: MnjOperacaoWithItens): number {
  return op.setores.reduce((acc, s) => acc + areaAplicadaHa(Number(s.areaHaSnapshot), Number(s.percentualAplicado)), 0);
}

/**
 * "Ureia 52 kg · MAP 20 kg · KCl 66 kg" — UMA linha, na unidade de cada produto.
 *
 * Nunca somada: kg e L não se somam, e foi exatamente isso que fez o card antigo
 * chamar de "kg" um número que incluía 9,95 L de óleo mineral. Aqui cada item
 * carrega a própria unidade e a linha trunca — o detalhe mostra tudo.
 */
export function linhaDeInsumos(op: MnjOperacaoWithItens): string {
  return op.itens
    .map(it => `${it.produto.nome} ${Number(it.qtdPrevista).toLocaleString("pt-BR", { maximumFractionDigits: 2 })} ${it.produto.unidade}`)
    .join(" · ");
}

function nomesExecutores(op: MnjOperacaoWithItens): string[] {
  const atribuidos = op.executores.filter(e => e.papel === "atribuido");
  const lista = atribuidos.length > 0 ? atribuidos : op.executores;
  return lista.map(e => e.funcionario.apelido ?? e.funcionario.nome);
}

function diaCurto(d: string | null): string {
  if (!d) return "";
  const dt = new Date(d + "T00:00:00");
  return dt.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
}

export interface CardOperacaoProps {
  operacao: MnjOperacaoWithItens;
  grupo: GrupoHoje;
  onAbrir: (op: MnjOperacaoWithItens) => void;
  onIniciar: (op: MnjOperacaoWithItens) => void;
  onConcluir: (op: MnjOperacaoWithItens) => void;
  ocupado?: boolean;
  /** `true` no chip "Amanhã": mostra o card sem botão de ação (só leitura). */
  somenteLeitura?: boolean;
}

export function CardOperacao({
  operacao, grupo, onAbrir, onIniciar, onConcluir, ocupado, somenteLeitura,
}: CardOperacaoProps) {
  const Icone = iconeDaCategoria(operacao.atividade?.categoria);
  const atrasada = grupo === "atrasada";
  const emAndamento = operacao.status === "em_andamento";
  const executores = nomesExecutores(operacao);
  const insumos = linhaDeInsumos(operacao);
  // 🔴 A linha de resumo é do INSUMO quando ele existe, e do TEXTO LIVRE quando não.
  // Insumo estruturado deixou de ser obrigatório na criação (a instrução escrita à
  // mão é o caso comum); sem este fallback, tudo que a pessoa digitou ficaria
  // invisível até alguém abrir o detalhe.
  const resumo = insumos || (operacao.observacao ?? "");
  const area = areaTotalAplicada(operacao);

  return (
    <div
      className={cn(
        "rounded-lg border bg-card overflow-hidden",
        // Atrasada é a única coisa que grita nesta tela — e grita pela BORDA, não
        // pelo fundo: fundo colorido atrás de texto pequeno no sol da roça é pior
        // de ler que fundo branco com borda forte.
        atrasada && "border-erro/40",
      )}
      data-testid={`card-operacao-${operacao.id}`}
    >
      {/* O corpo inteiro é o alvo de "abrir detalhe" — não um link escondido. */}
      <button
        type="button"
        onClick={() => onAbrir(operacao)}
        className="w-full text-left p-3 space-y-2 hover:bg-muted/40 transition-colors"
      >
        <div className="flex items-start gap-2.5">
          <Icone className="h-5 w-5 shrink-0 mt-0.5 text-muted-foreground" />
          <div className="min-w-0 flex-1 space-y-1">
            <div className="flex items-start justify-between gap-2">
              <span className="font-semibold text-base leading-snug">{tituloDaOperacao(operacao)}</span>
              {operacao.prioridade === "alta" && <StatusBadge tom="atencao">alta</StatusBadge>}
            </div>

            {/* Setores como chips, com a área somada ao lado (spec §6.1). */}
            {operacao.setores.length > 0 && (
              <div className="flex flex-wrap items-center gap-1">
                {operacao.setores.map(s => (
                  <Badge key={s.setorId} variant="outline" className="font-normal">{s.setor.nome}</Badge>
                ))}
                {area > 0 && (
                  <span className="text-xs text-muted-foreground tabular-nums">
                    {area.toLocaleString("pt-BR", { maximumFractionDigits: 2 })} ha
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Insumos (ou a instrução) em UMA linha — trunca, nunca quebra o card em três linhas. */}
        {resumo && (
          <p className="text-sm text-muted-foreground truncate" title={resumo}>{resumo}</p>
        )}

        <div className="flex items-center justify-between gap-2 text-xs">
          <span className="text-muted-foreground truncate">
            {executores.length > 0 ? executores.join(", ") : "sem executor"}
          </span>
          {atrasada ? (
            // O dia de ORIGEM no selo: "atrasada" sem data não diz se é de ontem
            // ou da semana passada, e isso muda o que a pessoa faz.
            <span className="inline-flex items-center gap-1 shrink-0 font-semibold text-erro">
              <AlertTriangle className="h-3.5 w-3.5" />
              {diaCurto(operacao.dataPrevista)}
            </span>
          ) : emAndamento ? (
            <StatusBadge tom="info" ponto>em andamento</StatusBadge>
          ) : null}
        </div>
      </button>

      {/* UM botão, largura total (spec §6.1). `min-h-11` = 44px de alvo de toque.
          `text-white` sobre `bg-sucesso`/`bg-info`: os dois tokens são escuros
          (L 26% e 36%), então o contraste passa. Não existe `-foreground` para
          eles no tailwind.config — os badges usam o par `-suave`/texto, que é
          fraco demais para o botão principal da tela. */}
      {!somenteLeitura && (
        <div className="px-3 pb-3">
          {emAndamento ? (
            <Button
              className="w-full min-h-11 bg-info text-white hover:bg-info/90"
              disabled={ocupado}
              onClick={() => onConcluir(operacao)}
              data-testid={`btn-concluir-${operacao.id}`}
            >
              <Check className="h-4 w-4 mr-1.5" />Concluir
            </Button>
          ) : (
            <Button
              className="w-full min-h-11 bg-sucesso text-white hover:bg-sucesso/90"
              disabled={ocupado}
              onClick={() => onIniciar(operacao)}
              data-testid={`btn-iniciar-${operacao.id}`}
            >
              <Play className="h-4 w-4 mr-1.5" />Iniciar
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
